import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useLayoutEffect, useMemo, useRef, useEffect} from 'react';
import {ListChildComponentProps, VariableSizeList} from 'react-window';

import * as analytics from 'analytics/Analytics';
import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {MolbioFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import AaSequenceStore from 'molbio/aa-sequences/AaSequenceStore';
import {Protein} from 'molbio/aa-sequences/util/AaSequenceConstants';
import EntityStore from 'stores/EntityStore';
import getTestProps from 'test/lib/cypress/getTestProps';
import {
  deleteAnnotation,
  DisplayAaSequenceAnnotation,
  getSortedDisplayAnnotations,
  handleAutoAnnotateAaSequenceModal,
  saveAnnotation,
} from 'toolbar/aa-sequence/AaSequenceAnnotationsPanelUtil';
import {
  selectAnnotation,
  openCreateAnnotation,
  aaSequenceAnnotationUIGlobalState,
  aaSequenceSelectionUIGlobalState,
  closeAnnotationForm,
  toggleAnnotationHover,
} from 'toolbar/aa-sequence/AaSequenceUIGlobalState';
import CreateEditAaSequenceAnnotationForm from 'toolbar/aa-sequence/CreateEditAaSequenceAnnotationForm';
import FeatureRowItem, {BASE_FEATURE_ROW_HEIGHT} from 'toolbar/feature-panel/FeatureRowItem';
import Button, {ButtonStyle, ButtonSize, ButtonLink} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';

const EDIT_FORM_BASE_FIELD_HEIGHT = 43;
const NUM_EDIT_FIELDS = 4;
const EMPTY_NOTES_FIELD_HEIGHT = 50;
const NOTES_FIELD_HEIGHT = 80;
const EDIT_FORM_BUTTONS_HEIGHT = 45;

interface AaSequenceAnnotationPanelRowVirtualizedListData {
  sortedAnnotationsData: Array<DisplayAaSequenceAnnotation>;
  onHoverAnnotation: (id: string, hover: boolean) => void;
  onSelectAnnotation: (id: string, selected: boolean) => void;
  onSaveAnnotation: () => void;
  onDeleteAnnotation: (id: string) => void;
  proteinId: string;
  proteinLength: number;
  allAnnotationTypes: Array<string>;
}

/**
 * Renders a single annotation row based on the props provided by the virtualized list.
 *
 * Note: This function needs to be defined at the top level (instead of inline in the panel component as
 * a closure). Otherwise, the edit form will lose focus on each update.
 * See: https://github.com/bvaughn/react-window/issues/420
 */
function AnnotationPanelRow({
  index,
  data,
  style,
}: ListChildComponentProps<AaSequenceAnnotationPanelRowVirtualizedListData>): JSX.Element {
  const annotation = data.sortedAnnotationsData[index];
  // Update the computed style to cap the height and add a scrollbar.
  const updatedStyle = {
    ...style,
    maxHeight: style.height,
    overflow: 'auto',
  };
  const contextMenuOptions = [
    {
      name: 'Remove annotation',
      click: () => {
        data.onDeleteAnnotation(annotation.cid);
      },
      hide: !annotation.editable,
    },
  ];
  return (
    <div style={updatedStyle}>
      <FeatureRowItem
        key={annotation.cid}
        feature={annotation}
        getLocationString={(anno) => `${anno.displayStart}-${anno.displayEnd}`}
        onHover={data.onHoverAnnotation}
        onSelect={data.onSelectAnnotation}
        editForm={
          <CreateEditAaSequenceAnnotationForm
            onClickSave={data.onSaveAnnotation}
            onClickCancel={() => {
              data.onSelectAnnotation(annotation.id, false);
            }}
            proteinId={data.proteinId}
            proteinLength={data.proteinLength}
            allAnnotationTypes={data.allAnnotationTypes}
            isReadOnly={!annotation.editable}
          />
        }
        contextMenuOptions={contextMenuOptions}
      />
    </div>
  );
}

function getDataFromAaSequenceStore(
  aaSequenceStore: typeof AaSequenceStore,
  proteinId: string
): {
  protein?: Protein;
  latestVersionId: number;
  canEditFeatures: boolean;
  allAnnotationTypes: Array<string>;
} {
  const aaSequence = aaSequenceStore.getAaSequenceById(proteinId);
  const latestVersionId = aaSequenceStore.getLatestVersionId(proteinId);
  const canEditFeatures =
    (aaSequence?.permissions && aaSequence?.permissions.polymerFeaturesEditable && aaSequence?.isLatest) ??
    false;
  const allAnnotationTypes =
    aaSequence == null
      ? []
      : [...new Set(aaSequence.version.annotations.map((data) => data.type).filter((type) => type))];
  return {protein: aaSequence, canEditFeatures, allAnnotationTypes, latestVersionId};
}

export interface AaSequenceAnnotationsPanelProps {
  proteinId: string;
  jumpToSelection: (start: number, end: number) => void;
}

/**
 * Toolbar panel for viewing and updating the annotations of an AA sequence.
 */
function AaSequenceAnnotationsPanel({
  proteinId,
  jumpToSelection,
}: AaSequenceAnnotationsPanelProps): JSX.Element {
  const listRef = useRef<VariableSizeList>(null);
  const {protein, canEditFeatures, allAnnotationTypes, latestVersionId} = useStoreSelector(
    AaSequenceStore,
    useCallback((store: typeof AaSequenceStore) => getDataFromAaSequenceStore(store, proteinId), [proteinId])
  );
  const currentUser = useStoreSelector(
    EntityStore,
    useCallback((store: typeof EntityStore) => store.currentUser(), [])
  );
  const [aaSequenceAnnotationPanelState, setAaSequenceAnnotationPanelState] =
    aaSequenceAnnotationUIGlobalState.useGlobalClientState(proteinId);
  const [aaSequenceSelectionUIState] = aaSequenceSelectionUIGlobalState.useGlobalClientState(proteinId);
  const {isCreateActive, selectedAnnotationId, hoveredAnnotationId} = aaSequenceAnnotationPanelState;
  const sortedAnnotations = useMemo(
    () => getSortedDisplayAnnotations(protein, canEditFeatures, selectedAnnotationId, hoveredAnnotationId),
    [protein, canEditFeatures, selectedAnnotationId, hoveredAnnotationId]
  );
  const featureLibraryUrl = currentUser && `/${currentUser.url_identifier}/feature-libraries/`;
  const selectedAnnotationIndex = sortedAnnotations.findIndex(({id}) => id === selectedAnnotationId);

  useLayoutEffect(() => {
    // react-window caches the row sizes. Whenever the selection changes we need to bust the cache for all
    // rows because the currently selected row and the previously selected row both changed size.
    listRef.current?.resetAfterIndex(0);
    if (selectedAnnotationIndex !== -1) {
      listRef.current?.scrollToItem(selectedAnnotationIndex, 'start');
    }
  }, [selectedAnnotationIndex]);

  function getAnnotationPanelRowHeight(index: number): number {
    if (!sortedAnnotations[index]?.selected) {
      return BASE_FEATURE_ROW_HEIGHT;
    }

    // Calculate an approximate size for the expanded row based on the size of the elements in the edit form.
    return (
      BASE_FEATURE_ROW_HEIGHT +
      EDIT_FORM_BASE_FIELD_HEIGHT * NUM_EDIT_FIELDS +
      (aaSequenceAnnotationPanelState.notes ? NOTES_FIELD_HEIGHT : EMPTY_NOTES_FIELD_HEIGHT) +
      (sortedAnnotations[index].editable ? EDIT_FORM_BUTTONS_HEIGHT : 0) +
      20
    );
  }

  useEffect(() => {
    if (selectedAnnotationId != null && selectedAnnotationIndex === -1) {
      setAaSequenceAnnotationPanelState({...aaSequenceAnnotationPanelState, selectedAnnotationId: null});
    }
  }, [
    aaSequenceAnnotationPanelState,
    selectedAnnotationId,
    selectedAnnotationIndex,
    setAaSequenceAnnotationPanelState,
  ]);

  function onOpenCreate(): void {
    if (!isCreateActive) {
      openCreateAnnotation(proteinId, aaSequenceSelectionUIState);
    }
  }

  function onCancelCreate(): void {
    if (isCreateActive) {
      setAaSequenceAnnotationPanelState({...aaSequenceAnnotationPanelState, isCreateActive: false});
    }
  }

  function onSelectAnnotation(annotationId: string, selected: boolean): void {
    if (!selected) {
      closeAnnotationForm(proteinId);
    } else {
      const annotation = protein?.version.annotations.find(({id}) => id === annotationId);
      if (annotation != null) {
        selectAnnotation(proteinId, annotation);
        jumpToSelection(annotation.start, annotation.end);
      }
    }
  }

  function onDeleteAnnotation(annotationId: string): void {
    deleteAnnotation(proteinId, latestVersionId, annotationId, selectedAnnotationId);
  }

  function onHoverAnnotation(annotationId: string, hovered: boolean): void {
    toggleAnnotationHover(proteinId, annotationId, hovered);
  }

  async function onClickAutoAnnotate(): Promise<void> {
    analytics.track('protein', 'clicked_auto_annotate', {
      product: ANALYTICS_MODULE.MOLBIO,
      protein_id: proteinId,
    });
    await handleAutoAnnotateAaSequenceModal(proteinId, sortedAnnotations.length, latestVersionId);
  }

  function onSaveAnnotation(): void {
    const annotationToSave = {
      name: aaSequenceAnnotationPanelState.name,
      type: aaSequenceAnnotationPanelState.annotationType,
      notes: aaSequenceAnnotationPanelState.notes,
      start: aaSequenceAnnotationPanelState.start,
      end: aaSequenceAnnotationPanelState.end,
      color: aaSequenceAnnotationPanelState.color,
    };
    saveAnnotation(
      proteinId,
      latestVersionId,
      protein!.version.aminoAcids.length,
      selectedAnnotationId,
      annotationToSave
    );
  }

  if (protein == null) {
    return <></>;
  }

  return (
    <div
      className={css(styles.proteinAnnotationsPanel)}
      {...getTestProps({component: 'ProteinAnnotationPanel', element: 'root'})}
    >
      {canEditFeatures && (
        <div className={css(styles.createNewButtonContainer)}>
          <Button
            className={css(styles.createNewButton)}
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.MINI}
            onClick={onOpenCreate}
            text="Create new"
            dataTestAttrs={{component: 'ProteinAnnotationPanel', element: 'createNewButton'}}
          />
        </div>
      )}
      <div
        className={css(styles.annotationRowsSection)}
        {...getTestProps({component: 'ProteinAnnotationsTable', element: 'root'})}
      >
        <VariableSizeList
          ref={listRef}
          itemData={{
            sortedAnnotationsData: sortedAnnotations,
            onHoverAnnotation,
            onSelectAnnotation,
            onSaveAnnotation,
            onDeleteAnnotation,
            proteinId,
            proteinLength: protein.version.aminoAcids.length,
            allAnnotationTypes,
          }}
          height={selectedAnnotationId == null ? 200 : 500}
          width="100%"
          itemCount={sortedAnnotations.length}
          overscanCount={20}
          itemSize={getAnnotationPanelRowHeight}
          estimatedItemSize={BASE_FEATURE_ROW_HEIGHT}
          itemKey={(index) => `proteinAnnotationPanelRow-${sortedAnnotations[index].id}`}
        >
          {AnnotationPanelRow}
        </VariableSizeList>
      </div>
      {canEditFeatures && isCreateActive && (
        <CreateEditAaSequenceAnnotationForm
          onClickSave={onSaveAnnotation}
          onClickCancel={onCancelCreate}
          proteinId={proteinId}
          proteinLength={protein.version.aminoAcids.length}
          allAnnotationTypes={allAnnotationTypes}
          isReadOnly={false}
        />
      )}
      {canEditFeatures && !isCreateActive && (
        <div className={css(styles.buttonRow)}>
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            size={ButtonSize.MINI}
            onClick={onClickAutoAnnotate}
            text="Auto-annotate"
            dataTestAttrs={{component: 'AnnotationPanelContent', element: 'autoAnnotateButton'}}
          />
          {isFeatureEnabled({feature: MolbioFeature.MOLBIO_ADMIN}) && (
            <ButtonLink
              buttonStyle={ButtonStyle.LINK}
              href={featureLibraryUrl}
              target="_blank"
              text="Edit feature libraries"
            />
          )}
        </div>
      )}
    </div>
  );
}

export default AaSequenceAnnotationsPanel;

const styles = StyleSheet.create({
  proteinAnnotationsPanel: {
    // Base panel already has padding, but we want the content to reach the edges
    margin: '0 -15px -10px',
  },
  annotationRowsSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  createNewButtonContainer: {
    display: 'flex',
    padding: '12px 8px',
  },
  createNewButton: {
    marginLeft: 'auto',
  },
  buttonRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 12px',
  },
});
